import { Card } from "@/components/Card";
import React, { useEffect, useState } from "react";
import Select from "@/components/Select";
import { applications } from "./Applications";
import IntegrationsModal from "./NetSuite/NetsuitePreviewModal";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import useApi from "@/hooks/useApi";
import {
  fetchNetSuiteConfig,
  fetchFormstackConfig,
  fetchAccelaConfig,
  fetchShopifyConfig
} from "./IntegrationConfigs";
import { appCategories } from "./Categories";
import { IconProps } from "@velaro/velaro-component-lib";
import IntegrationsNewApp from "./IntegrationsNewApp";
import useSubscriptions from "@/hooks/useSubscriptions";

interface NetSuiteConfig {
  id: number;
  displayName: string;
  company: string;
  teamId: number;
  createdAt: string;
}

interface FormstackConfig {
  displayName: string;
  token: string;
}

interface AccelaConfig {
  id: number;
  agencyName: string;
  clientId: string;
  clientSecret: string;
  emailAddress: string;
  accessToken: string;
  refreshToken: string;
  scope?: string;
  teamId: number;
  token?: string;
  refreshTokenExpired?: boolean;
}

interface ShopifyConfig {
  id: number;
  displayName: string;
  company: string;
  teamId: number;
  createdAt: string;
}

type App = {
  id: string;
  name: string;
  category: string;
  icon?: string | ((props: IconProps) => JSX.Element); // Accept both image paths and components
  subTitle: string;
};

interface OptionType {
  value: string | number;
  label: string;
}

const customSingleValue = ({ data }: { data: OptionType }) => (
  <div className="flex items-center gap-2">
    <img src="" alt="Selected" className="w-4 h-4" />
    {data.label}
  </div>
);

const customOption = (props: {
  data: any;
  isSelected: any;
  innerRef: any;
  innerProps: any;
}) => {
  const { data, isSelected, innerRef, innerProps } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${
        isSelected ? "bg-blue-100" : "bg-white"
      }`}
    >
      {isSelected && <img src="" alt="Selected" className="w-4 h-4" />}
      {data.label}
    </div>
  );
};
export default function Landing() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [visibleCount, setVisibleCount] = useState(11);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);
  const [netSuiteConfigs, setNetSuiteConfigs] = useState<NetSuiteConfig[]>([]);
  const [formstackConfig, setFormstackConfig] =
    useState<FormstackConfig | null>(null);
  const [accelaConfigs, setAccelaConfigs] = useState<AccelaConfig | null>(null);
  const [shopifyConfigs, setShopifyConfigs] = useState<ShopifyConfig | null>(
    null
  );
  const { subscription } = useSubscriptions();
  const api = useApi();
  const { teamId } = useSelectedTeam();
  const [appStatus, setAppStatuses] = useState<{ [key: string]: string }>({});
  const [showNewAppPanel, setShowNewAppPanel] = useState(false);

  useEffect(() => {
    const fetchStatuses = async () => {
      const newStatuses: { [key: string]: string } = {};

      if (netSuiteConfigs.length > 0) {
        newStatuses["netsuite"] = await checkIfMappingExists("netsuite");
      }

      if (formstackConfig != null) {
        newStatuses["formstack"] = "Connected";
      }

      if (accelaConfigs != null) {
        newStatuses["accela-civic"] = "Connected";
      }

      if (shopifyConfigs != null) {
        newStatuses["shopify"] = "Connected";
      }

      setAppStatuses(newStatuses);
    };

    fetchStatuses();
  }, [netSuiteConfigs, formstackConfig, accelaConfigs, shopifyConfigs]);

  const [configId, setConfigId] = useState<string | "">("");
  const checkIfMappingExists = async (appId: string): Promise<string> => {
    if (appId === "netsuite" && netSuiteConfigs.length > 0) {
      const latestConfigId =
        [...netSuiteConfigs].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0]?.id || "";

      setConfigId(latestConfigId ? String(latestConfigId) : "");

      try {
        const resp = await api.messaging.get(
          `NetSuite/Integrations/Mapping/${latestConfigId}`
        );
        const count = await resp.json();

        return count > 0 ? "Connected" : "Pending";
      } catch (error) {
        console.error("Error fetching NetSuite config:", error);
        return "Error";
      }
    }

    return "";
  };
  const filteredApps =
    selectedCategory === "all"
      ? applications
      : applications.filter((app) => app.category === selectedCategory);

  const showApps = filteredApps.filter((app) => {
    if (!subscription) return true;

    const disabledApps: Record<string, boolean> = {
      "accela-civic": !subscription.enableAccela,
      shopify: !subscription.enableShopify
    };

    return !disabledApps[app.id];
  });

  React.useEffect(() => {
    setVisibleCount(11);
  }, [selectedCategory]);

  const handleLoadMore = () => {
    setVisibleCount((prevCount) => prevCount + 6);
  };

  const handleCardClick = async (app: App) => {
    if (app.id === "new") {
      setShowNewAppPanel(true);
      return;
    }

    const hasConfig = checkIfConfigExists(app);
    if (hasConfig) {
      window.location.href = `/integrations/${app.id}`;
    } else {
      if (app.id === "formstack") {
        window.location.href = `/integrations/${app.id}/${app.id}create`;
      } else if (app.id === "accela-civic") {
        window.location.href = `/integrations/${app.id}/accelacreate`;
      } else if (app.id === "shopify") {
        window.location.href = `/integrations/${app.id}/shopifyoverview`;
      } else {
        setSelectedApp(app);
        setShowIntegrationsModal(true);
      }
    }
  };

  const checkIfConfigExists = (app: App): boolean => {
    if (app.id === "netsuite") {
      return netSuiteConfigs.length > 0;
    } else if (app.id === "formstack") {
      return formstackConfig !== null;
    } else if (app.id === "accela-civic") {
      return accelaConfigs !== null;
    } else if (app.id === "shopify") {
      return shopifyConfigs !== null;
    }
    return false;
  };

  React.useEffect(() => {
    async function loadConfigs() {
      if (teamId) {
        const netSuiteData = await fetchNetSuiteConfig(api, teamId);
        if (netSuiteData) setNetSuiteConfigs(netSuiteData);
      }
      const formstackData = await fetchFormstackConfig(api);
      if (formstackData) setFormstackConfig(formstackData);

      const accelaData = await fetchAccelaConfig(
        api,
        subscription?.enableAccela
      );
      if (accelaData) setAccelaConfigs(accelaData);

      const shopifyData = await fetchShopifyConfig(
        api,
        subscription?.enableShopify
      );
      if (shopifyData) setShopifyConfigs(shopifyData);
    }

    loadConfigs();
  }, [api, teamId]);

  const handleModalClose = () => {
    setShowIntegrationsModal(false);
  };

  const modifiedCategories = appCategories.map((cat) => ({
    ...cat,
    label: cat.value === selectedCategory ? ` ${cat.label}` : cat.label
  }));
  return (
    <div className="panel bg-white dark:bg-stone-300 rounded-2xl shadow-md mb-4 p-4 xl:max-w-[1350px] xl:justify-center xl:mx-auto">
      <div className="flex justify-between items-center mb-4">
        <div className="text-center text-slate-800 text-lg font-medium font-['Rubik'] leading-7">
          All Apps
          <span className="text-center text-slate-500 text-lg font-medium font-['Rubik'] leading-7 ml-2">
            {filteredApps.length - 1} apps
          </span>
        </div>
        <style>{`select option[value=""] { display: none; }`}</style>
        <Select
          className="w-48 h-12 text-sm px-3 py-2 border rounded-md !w-48"
          value={selectedCategory}
          options={modifiedCategories}
          onChange={(value) => setSelectedCategory(value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 netsuite-card">
        {showApps.slice(0, visibleCount).map((app, index) => (
          <Card
            key={app.id}
            to=""
            title={app.name}
            subtitle={app.subTitle}
            icon={app.icon}
            variant="integrations"
            isFirstCard={index === 0}
            onClick={() => {
              handleCardClick(app);
            }}
            appStatus={String(appStatus[app.id] || "")}
          />
        ))}
      </div>

      {visibleCount < filteredApps.length && (
        <div className="flex justify-center mt-4">
          <button
            onClick={handleLoadMore}
            className="text-blue-600 font-medium focus:outline-none"
          >
            Load More
          </button>
        </div>
      )}

      {showIntegrationsModal && selectedApp && (
        <IntegrationsModal
          show={showIntegrationsModal}
          onClose={handleModalClose}
          appId={selectedApp.id}
          appName={selectedApp.name}
          icon={selectedApp.icon || ""}
          teamId={teamId}
          appStatus={appStatus[selectedApp.id] || ""}
          configId={configId}
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-1/4 bg-white shadow-lg transition-transform duration-300 ${
          showNewAppPanel ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <IntegrationsNewApp onClose={() => setShowNewAppPanel(false)} />
      </div>
    </div>
  );
}
